// src/pages/HypotheticalPage.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { lookupCusip, fetchLivePrices, fetchHistoricalPrices, calculateGreeks } from '../lib/api';
import {
  calcDailyReturns, calcPortfolioReturns, calcVaR, calcCVaR,
  calcAnnualizedVol, calcSharpe, calcSortino, calcMaxDrawdown,
  calcBeta, getApproximateFFFactors, runFamaFrenchRegression, calcExposures, mean
} from '../lib/riskEngine';
import { ApiConfirmModal, RateLimitBanner, fmtMoney, fmtPct, fmtNum, colorClass, LoadingOverlay } from '../components';

export default function HypotheticalPage() {
  const { canEdit } = useAuth();
  const [savedPortfolios, setSavedPortfolios] = useState([]);
  const [activePortId, setActivePortId] = useState(null);
  const [positions, setPositions] = useState([]); // hypothetical positions
  const [livePositions, setLivePositions] = useState([]); // for comparison
  const [risk, setRisk] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null);
  const [newPortName, setNewPortName] = useState('');
  const [showNewPort, setShowNewPort] = useState(false);
  const [addForm, setAddForm] = useState({ cusip: '', ticker: '', quantity: '', direction: 'long', entry_price: '' });
  const [lookupLoading, setLookupLoading] = useState(false);
  const [totalNav, setTotalNav] = useState(0);

  useEffect(() => { loadSaved(); }, []);
  useEffect(() => {
    const nav = positions.reduce((s,p) => s + Math.abs((p.quantity||0) * (p.current_price||p.entry_price||0)), 0);
    setTotalNav(nav);
  }, [positions]);

  async function loadSaved() {
    const { data } = await supabase.from('hypothetical_portfolios').select('*').order('created_at', { ascending: false });
    setSavedPortfolios(data || []);
  }

  async function loadPortfolio(portId) {
    setActivePortId(portId);
    const { data } = await supabase.from('hypothetical_positions').select('*').eq('portfolio_id', portId);
    setPositions(data || []);
    setRisk(null);
  }

  async function createPortfolio() {
    if (!newPortName.trim()) return;
    const { data } = await supabase.from('hypothetical_portfolios').insert({ name: newPortName.trim(), benchmark: 'SPY' }).select().single();
    if (data) { setSavedPortfolios(p => [data, ...p]); loadPortfolio(data.id); }
    setNewPortName(''); setShowNewPort(false);
  }

  async function handleLookup() {
    if (!addForm.cusip) return;
    setLookupLoading(true);
    const result = await lookupCusip(addForm.cusip.trim().toUpperCase());
    if (result) setAddForm(f => ({...f, ticker: result.ticker || ''}));
    setLookupLoading(false);
  }

  async function addPosition() {
    if (!addForm.cusip || !addForm.quantity || !addForm.entry_price || !activePortId) return;
    const pos = {
      portfolio_id: activePortId,
      cusip: addForm.cusip.trim().toUpperCase(),
      ticker: addForm.ticker,
      quantity: parseFloat(addForm.quantity),
      direction: addForm.direction,
      entry_price: parseFloat(addForm.entry_price),
      current_price: parseFloat(addForm.entry_price)
    };
    const { data } = await supabase.from('hypothetical_positions').insert(pos).select().single();
    if (data) setPositions(p => [...p, data]);
    setAddForm({ cusip: '', ticker: '', quantity: '', direction: 'long', entry_price: '' });
  }

  async function removePosition(id) {
    await supabase.from('hypothetical_positions').delete().eq('id', id);
    setPositions(p => p.filter(pos => pos.id !== id));
  }

  async function handleRefreshAndAnalyze() {
    setShowConfirm(null);
    setCalculating(true);
    try {
      const tickers = [...new Set([...positions.map(p => p.ticker).filter(Boolean), 'SPY'])];
      const prices = await fetchLivePrices(tickers);

      // Update current prices
      const updated = positions.map(p => ({
        ...p,
        current_price: prices[p.ticker]?.price || p.entry_price
      }));
      setPositions(updated);

      const historicalMap = {};
      for (const ticker of tickers) {
        const hist = await fetchHistoricalPrices(ticker, 12);
        if (hist.length > 10) historicalMap[ticker] = hist.map(h => h.adjClose || h.close);
      }

      const returnsMap = {};
      for (const [ticker, pArr] of Object.entries(historicalMap)) returnsMap[ticker] = calcDailyReturns(pArr);

      const nav = updated.reduce((s, p) => s + Math.abs(p.quantity * (p.current_price || p.entry_price)), 0);
      const weights = {};
      for (const p of updated) {
        if (!p.ticker || p.ticker === 'CASH') continue;
        weights[p.ticker] = ((p.direction === 'long' ? 1 : -1) * p.quantity * (p.current_price || p.entry_price)) / nav;
      }

      const portReturns = calcPortfolioReturns(returnsMap, weights);
      const spyReturns = returnsMap['SPY'] || [];
      const ffFactors = getApproximateFFFactors(portReturns.length);
      const ff = portReturns.length >= 30 ? runFamaFrenchRegression(portReturns.slice(-252), ffFactors.slice(-252)) : null;
      const portPrices = portReturns.reduce((acc, r) => { acc.push(acc[acc.length-1] * (1+r)); return acc; }, [100]);

      const exposures = calcExposures(updated.map(p => ({...p, market_value: (p.direction==='long'?1:-1)*p.quantity*(p.current_price||p.entry_price)})), nav);

      setRisk({
        nav,
        exposures,
        var95: calcVaR(portReturns, 0.95, nav),
        var99: calcVaR(portReturns, 0.99, nav),
        cvar95: calcCVaR(portReturns, 0.95, nav),
        vol: calcAnnualizedVol(portReturns) * 100,
        sharpe: calcSharpe(portReturns),
        sortino: calcSortino(portReturns),
        maxDD: calcMaxDrawdown(portPrices) * 100,
        beta: calcBeta(portReturns, spyReturns),
        annReturn: mean(portReturns) * 252 * 100,
        ff
      });
    } catch (e) { console.error(e); }
    setCalculating(false);
  }

  const tickers = [...new Set(positions.map(p => p.ticker).filter(Boolean))];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Hypothetical</div>
          <div className="page-subtitle">Build & analyze hypothetical portfolios — no impact on live book</div>
        </div>
        {canEdit && (
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowNewPort(!showNewPort)}>+ New Portfolio</button>
            {activePortId && positions.length > 0 && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowConfirm('analyze')} disabled={calculating}>
                {calculating ? '⟳ Analyzing...' : '⟳ Price & Analyze'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="page-body section-gap">
        {canEdit && <RateLimitBanner provider="yahoo_finance" />}

        {/* New portfolio form */}
        {showNewPort && (
          <div className="card">
            <div className="card-body">
              <div style={{display:'flex',gap:8,alignItems:'flex-end'}}>
                <div className="form-group" style={{flex:1,marginBottom:0}}>
                  <label className="form-label">Portfolio Name</label>
                  <input className="form-input" value={newPortName} onChange={e=>setNewPortName(e.target.value)} placeholder="e.g. Tech Tilt Test" onKeyDown={e=>e.key==='Enter'&&createPortfolio()} />
                </div>
                <button className="btn btn-primary" onClick={createPortfolio}>Create</button>
              </div>
            </div>
          </div>
        )}

        {/* Saved portfolios */}
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {savedPortfolios.map(p => (
            <button key={p.id} className={`sleeve-pill ${activePortId===p.id?'active':''}`} onClick={() => loadPortfolio(p.id)}>
              {p.name}
            </button>
          ))}
          {!savedPortfolios.length && <div style={{color:'var(--text-muted)',fontSize:13}}>No hypothetical portfolios yet. Create one above.</div>}
        </div>

        {activePortId && (
          <>
            {/* Add position */}
            {canEdit && (
              <div className="card">
                <div className="card-header"><span className="card-title">Add Position to Hypothetical</span></div>
                <div className="card-body">
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Identifier</label>
                      <div style={{display:'flex',gap:6}}>
                        <input className="form-input" value={addForm.cusip} onChange={e=>setAddForm(f=>({...f,cusip:e.target.value.toUpperCase()}))} placeholder="CUSIP / ISIN / FIGI / Ticker" maxLength={32} style={{width:210,fontFamily:'var(--font-mono)'}} />
                        <button className="btn btn-secondary btn-sm" onClick={handleLookup} disabled={lookupLoading}>
                          {lookupLoading?'...':'Lookup'}
                        </button>
                      </div>
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Ticker</label>
                      <input className="form-input" value={addForm.ticker} onChange={e=>setAddForm(f=>({...f,ticker:e.target.value.toUpperCase()}))} placeholder="AAPL" style={{width:80,fontFamily:'var(--font-mono)'}} />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Direction</label>
                      <select className="form-select" value={addForm.direction} onChange={e=>setAddForm(f=>({...f,direction:e.target.value}))} style={{width:90}}>
                        <option value="long">Long</option>
                        <option value="short">Short</option>
                      </select>
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Quantity</label>
                      <input className="form-input" type="number" value={addForm.quantity} onChange={e=>setAddForm(f=>({...f,quantity:e.target.value}))} placeholder="1000" style={{width:90}} />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Entry Price</label>
                      <input className="form-input" type="number" value={addForm.entry_price} onChange={e=>setAddForm(f=>({...f,entry_price:e.target.value}))} placeholder="150.00" step="0.01" style={{width:100}} />
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={addPosition} disabled={!addForm.cusip||!addForm.quantity||!addForm.entry_price} style={{marginBottom:0}}>
                      + Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Positions table */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Hypothetical Positions ({positions.length})</span>
                {totalNav > 0 && <span style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>NAV: {fmtMoney(totalNav)}</span>}
              </div>
              {positions.length === 0 ? (
                <div className="empty-state"><div className="empty-state-desc">Add positions above to build your hypothetical portfolio.</div></div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead><tr><th>Ticker</th><th>CUSIP</th><th>Dir</th><th>Qty</th><th>Entry</th><th>Current</th><th>Mkt Value</th><th>Wt %</th>{canEdit&&<th></th>}</tr></thead>
                    <tbody>
                      {positions.map(p => {
                        const mv = (p.direction==='long'?1:-1) * p.quantity * (p.current_price||p.entry_price);
                        const wt = totalNav > 0 ? Math.abs(mv)/totalNav*100 : 0;
                        return (
                          <tr key={p.id}>
                            <td className="primary">{p.ticker||'—'}</td>
                            <td style={{fontSize:10,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{p.cusip}</td>
                            <td><span className={`badge badge-${p.direction}`}>{p.direction}</span></td>
                            <td>{fmtNum(p.quantity,0)}</td>
                            <td>${fmtNum(p.entry_price,2)}</td>
                            <td>{p.current_price ? `$${fmtNum(p.current_price,2)}` : '—'}</td>
                            <td className={colorClass(mv)}>{fmtMoney(mv)}</td>
                            <td>{fmtNum(wt,1)}%</td>
                            {canEdit && <td><button className="btn btn-danger btn-sm" onClick={() => removePosition(p.id)}>✕</button></td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Risk results */}
            {risk && (
              <div className="section-gap">
                <div style={{padding:'6px 0',fontSize:12,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>— Hypothetical Analysis Results —</div>
                <div className="stat-grid stat-grid-4">
                  {[
                    ['NAV', fmtMoney(risk.nav), ''],
                    ['Net Exposure', fmtMoney(risk.exposures?.netExposure), colorClass(risk.exposures?.netExposure)],
                    ['Gross Exposure', fmtMoney(risk.exposures?.grossExposure), ''],
                    ['Ann. Return', fmtPct(risk.annReturn), colorClass(risk.annReturn)],
                    ['VaR 95%', fmtMoney(risk.var95), 'negative'],
                    ['CVaR 95%', fmtMoney(risk.cvar95), 'negative'],
                    ['Ann. Vol', fmtNum(risk.vol,1)+'%', ''],
                    ['Sharpe', fmtNum(risk.sharpe,2), colorClass(risk.sharpe)],
                    ['Sortino', fmtNum(risk.sortino,2), colorClass(risk.sortino)],
                    ['Max Drawdown', fmtPct(-risk.maxDD), 'negative'],
                    ['Beta (SPY)', fmtNum(risk.beta,2), ''],
                    ['FF Alpha', risk.ff ? fmtPct(risk.ff.alpha*100) : '—', risk.ff ? colorClass(risk.ff.alpha) : ''],
                  ].map(([l,v,c]) => (
                    <div key={l} className="stat-card">
                      <div className="stat-label">{l}</div>
                      <div className={`stat-value ${c}`}>{v}</div>
                    </div>
                  ))}
                </div>

                {/* Sector exposure */}
                {risk.exposures?.sectorWeights && (
                  <div className="card">
                    <div className="card-header"><span className="card-title">Sector Weights (Hypothetical)</span></div>
                    <div className="card-body">
                      {Object.entries(risk.exposures.sectorWeights).sort(([,a],[,b])=>b-a).map(([k,v],i) => (
                        <div key={k} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                          <span style={{fontSize:11,color:'var(--text-secondary)',flex:1,fontFamily:'var(--font-mono)'}}>{k}</span>
                          <div style={{width:120,height:4,background:'var(--bg-base)',borderRadius:2}}>
                            <div style={{width:`${Math.min(Math.abs(v),100)}%`,height:'100%',background:'var(--accent)',borderRadius:2}} />
                          </div>
                          <span style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)',minWidth:48,textAlign:'right'}}>{v.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {risk.ff && (
                  <div className="stat-grid stat-grid-4">
                    {[['Market β', risk.ff.market_beta,2],['SMB',risk.ff.smb,2],['HML',risk.ff.hml,2],['MOM',risk.ff.mom,2],['R²',risk.ff.r_squared*100,1]].map(([l,v,d])=>(
                      <div key={l} className="stat-card">
                        <div className="stat-label">FF: {l}</div>
                        <div className="stat-value">{fmtNum(v,d)}{l==='R²'?'%':''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ApiConfirmModal
        isOpen={showConfirm === 'analyze'}
        onConfirm={handleRefreshAndAnalyze}
        onCancel={() => setShowConfirm(null)}
        action={`Fetch live prices + 12 months historical for ${tickers.length} tickers`}
        provider="yahoo_finance"
        estimatedCalls={tickers.length * 2}
      />
    </div>
  );
}
